<template>
  <div class="forum_assemble_page">
    <div ref="assembleScroll" class="forum_assemble_scroll">
      <div v-if="topPostList.length" class="forum_assemble_top">
        <TopPostList :list="topPostList" @post-click="onTopPostClick" @toggle="onTopPostToggle" />
      </div>

      <div v-if="tagOptions.length" class="forum_assemble_filter">
        <div class="forum_assemble_tabs">
          <button
            v-for="item in tagOptions"
            :key="item.tagName"
            type="button"
            :class="['forum_assemble_tab', { 'forum_assemble_tab--active': activeTagName === item.tagName }]"
            @click="onTagChange(item)"
          >
            <span>{{ item.tagName }}</span>
            <i />
          </button>
        </div>
        <div class="forum_assemble_sort">
          <button type="button" :class="{ 'forum_assemble_sort_btn--active': sortType === 1 }" @click="onSortChange(1)">最新</button>
          <span>/</span>
          <button type="button" :class="{ 'forum_assemble_sort_btn--active': sortType === 2 }" @click="onSortChange(2)">最热</button>
        </div>
      </div>

      <div class="forum_assemble_refresh">
        <div v-if="loading" class="forum_assemble_loading">加载中...</div>
        <template v-else>
          <PostItem
            v-for="post in list"
            :key="post.postsId"
            :data="post"
            @click="onPostClick"
            @comment="onComment"
            @praise="onPraise"
            @collect="onCollect"
          />
          <EmptyState v-if="list.length === 0" text="暂无内容，快来发第一帖吧" />
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { collectOrCancelPosts, getPostAssembleInfo, getPostAssemblePostList, praiseOrCancelPosts } from '@/api/forum';
import { extractForumRecords } from '@/utils/forum/postList';
import { isInteractionOk, toastInteraction } from '@/utils/forum/forumInteraction';
import EmptyState from './components/EmptyState.vue';
import PostItem from './components/PostItem.vue';
import TopPostList from './components/TopPostList.vue';
import trackingMixin, { EVENT_TYPE_ENUM } from './mixins/tracking';
import forumLoginRefreshMixin from './mixins/forumLoginRefresh';

export default {
  name: 'ForumPostAssemble',
  mixins: [trackingMixin, forumLoginRefreshMixin],
  components: { EmptyState, PostItem, TopPostList },
  data() {
    return {
      detail: {},
      tagOptions: [],
      topPostList: [],
      activeTagName: '',
      sortType: 1,
      list: [],
      loading: false,
      requestSeq: 0,
      bootstrappedAssembleId: '',
      bootstrapping: false,
    };
  },
  computed: {
    assembleId() {
      return String(this.$route.params.assembleId || '');
    },
    fallbackTitle() {
      return String(this.$route.query.title || '帖子集合');
    },
  },
  activated() {
    this.bootstrap();
  },
  created() {
    this.bootstrap();
  },
  methods: {
    getForumLoginRefreshConfig() {
      return {
        routeName: 'forum-assemble',
        when: () => Boolean(this.assembleId),
        onRefresh: () => this.refreshAfterLogin(),
      };
    },
    async refreshAfterLogin() {
      await this.fetchInfo();
      await this.fetchPosts();
    },
    async bootstrap() {
      if (!this.assembleId) return;
      if (this.bootstrapping) return;
      this.bootstrapping = true;
      try {
        if (this.bootstrappedAssembleId !== this.assembleId) {
          this.detail = {};
          this.tagOptions = [];
          this.topPostList = [];
          this.activeTagName = '';
          this.sortType = 1;
          this.list = [];
        }
        this.bootstrappedAssembleId = this.assembleId;
        await this.fetchInfo();
        this.EVENT_TRACKING(EVENT_TYPE_ENUM.platform_group_square_page_show, {
          ...this.getAssembleTrackingFields(),
        });
        this.trackAssembleTagClick();
        await this.fetchPosts();
      } finally {
        this.bootstrapping = false;
      }
    },
    getAssembleTrackingFields() {
      return {
        page_type: '帖子集合页',
        group_id: this.detail.groupId || '',
        group_name: this.detail.groupName || '',
        assemble_id: this.assembleId,
        assemble_name: this.detail.assembleName || this.fallbackTitle,
      };
    },
    getPostTrackingFields(post, location) {
      return {
        page_type: '帖子集合页',
        ...(location ? { location } : {}),
        post_id: post.postsId || post.postId || '',
        post_title: post.title || '',
        group_id: this.detail.groupId || '',
        group_name: this.detail.groupName || '',
      };
    },
    trackAssembleButton(buttonType) {
      this.EVENT_TRACKING(EVENT_TYPE_ENUM.platform_group_home_button, {
        ...this.getAssembleTrackingFields(),
        button_type: buttonType,
      });
    },
    getAssembleTagId(tag) {
      return (tag && (tag.tagId || tag.id || tag.tagName)) || '';
    },
    getAssembleTagTrackingFields(tag) {
      const currentTag = tag || this.tagOptions.find((item) => item.tagName === this.activeTagName) || {};
      return {
        ...this.getAssembleTrackingFields(),
        primary_tag_id: this.getAssembleTagId(currentTag),
        primary_tag_name: currentTag.tagName || '',
        second_tag_id: '',
        second_tag_name: '',
      };
    },
    trackAssembleTagClick(tag) {
      const currentTag = tag || this.tagOptions.find((item) => item.tagName === this.activeTagName);
      if (!currentTag) return;
      this.EVENT_TRACKING(EVENT_TYPE_ENUM.platform_group_home_tag_click, {
        ...this.getAssembleTagTrackingFields(currentTag),
      });
    },
    applyInfo(info = {}) {
      this.detail = info;
      this.topPostList = info.topPostList || [];
      this.tagOptions = (info.tagList || []).filter((item) => item && item.tagName);
      const current = this.tagOptions.find((item) => item.tagName === this.activeTagName);
      this.activeTagName = (current && current.tagName) || ((this.tagOptions[0] || {}).tagName || '');
    },
    async fetchInfo() {
      const res = await getPostAssembleInfo(this.assembleId);
      if (res && res.data) this.applyInfo(res.data);
    },
    async fetchPosts() {
      const requestSeq = ++this.requestSeq;
      this.loading = true;
      try {
        const res = await getPostAssemblePostList({
          assembleId: this.assembleId,
          tagName: this.activeTagName,
          sortType: this.sortType,
        });
        if (requestSeq !== this.requestSeq) return;
        this.list = extractForumRecords(res && res.data);
      } catch (error) {
        if (requestSeq === this.requestSeq) this.list = [];
      } finally {
        if (requestSeq === this.requestSeq) this.loading = false;
      }
    },
    onTagChange(tag) {
      if (!tag || this.activeTagName === tag.tagName) return;
      this.EVENT_TRACKING(EVENT_TYPE_ENUM.platform_group_home_tag_click, {
        ...this.getAssembleTagTrackingFields(tag),
      });
      this.activeTagName = tag.tagName;
      this.fetchPosts();
    },
    onSortChange(type) {
      if (this.sortType === type) return;
      this.trackAssembleButton(type === 1 ? '最新' : '最热');
      this.sortType = type;
      this.fetchPosts();
    },
    async onPraise(post) {
      this.EVENT_TRACKING(EVENT_TYPE_ENUM.platform_group_home_post_opt_click, {
        ...this.getPostTrackingFields(post),
        button_type: post.praise ? '取消点赞' : '点赞',
      });
      const res = await praiseOrCancelPosts(post.postsId, post.praise ? 2 : 1).catch((err) => err);
      if (!toastInteraction(res, { successMsg: post.praise ? '已取消点赞' : '已点赞' })) return;
      post.praise = !post.praise;
      post.praiseNum = (post.praiseNum || 0) + (post.praise ? 1 : -1);
    },
    async onCollect(post) {
      this.EVENT_TRACKING(EVENT_TYPE_ENUM.platform_group_home_post_opt_click, {
        ...this.getPostTrackingFields(post),
        button_type: post.collect ? '取消收藏' : '收藏',
      });
      const res = await collectOrCancelPosts(post.postsId, post.collect ? 2 : 1).catch((err) => err);
      if (!isInteractionOk(res)) return toastInteraction(res, { fallbackMsg: '操作失败' });
      post.collect = !post.collect;
      post.collectNum = (post.collectNum || 0) + (post.collect ? 1 : -1);
    },
    onComment(post) {
      this.EVENT_TRACKING(EVENT_TYPE_ENUM.platform_group_home_post_opt_click, {
        ...this.getPostTrackingFields(post),
        button_type: '评论',
      });
    },
    onPostClick(post) {
      this.EVENT_TRACKING(EVENT_TYPE_ENUM.platform_group_home_post_click, {
        ...this.getPostTrackingFields(post, '帖子列表'),
      });
    },
    onTopPostClick(post) {
      this.EVENT_TRACKING(EVENT_TYPE_ENUM.platform_group_home_post_click, {
        ...this.getPostTrackingFields(post, '置顶贴'),
      });
    },
    onTopPostToggle() {
      this.trackAssembleButton('展开更多置顶');
    },
  },
};
</script>

<style scoped>
.forum_assemble_page {
  height: 100vh;
  overflow: hidden;
  background: #f5f5f5;
}

.forum_assemble_scroll {
  height: 100%;
  overflow-y: auto;
}

.forum_assemble_top {
  width: 100%;
  box-sizing: border-box;
  margin-top: 8px;
  border-radius: 12px 12px 0 0;
  overflow: hidden;
  padding: 0 12px;
  background: #fff;
}

.forum_assemble_filter {
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  min-height: 52px;
  padding: 8px 12px;
  background: #fff;
}

.forum_assemble_tabs {
  display: flex;
  align-items: center;
  min-width: 0;
  flex: 1;
  overflow-x: auto;
  white-space: nowrap;
}

.forum_assemble_tabs::-webkit-scrollbar {
  display: none;
}

.forum_assemble_tab {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  border: 0;
  padding: 0;
  color: #777;
  background: transparent;
  font-size: 12px;
  line-height: 1;
}

.forum_assemble_tab + .forum_assemble_tab {
  margin-left: 16px;
}

.forum_assemble_tab--active {
  color: #111827;
  font-size: 16px;
  font-weight: 600;
}

.forum_assemble_tab i {
  width: 16px;
  height: 3px;
  margin-top: 4px;
  border-radius: 999px;
  background: transparent;
}

.forum_assemble_tab--active i {
  background: #ff5b5b;
}

.forum_assemble_sort {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin-left: 8px;
  color: #ccc;
  font-size: 12px;
  line-height: 1;
}

.forum_assemble_sort button {
  border: 0;
  padding: 0;
  color: #999;
  background: transparent;
  font-size: 12px;
  font-weight: 500;
}

.forum_assemble_sort span {
  margin: 0 2px;
}

.forum_assemble_sort_btn--active {
  color: #0249ff !important;
  font-weight: 600 !important;
}

.forum_assemble_refresh {
  margin-top: 8px;
}

.forum_assemble_loading {
  padding: 16px 12px;
  color: #999;
  font-size: 13px;
  text-align: center;
}
</style>
